import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, yen, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { DatePicker } from "../components/DatePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Ticket, Trash2, Plus } from "lucide-react";
import { jstToday } from "../lib/jst";

const blank = {
  code: "",
  kind: "percent",
  value: "10",
  min_order: "",
  valid_from: "",
  valid_to: "",
  usage_limit: "",
  note: "",
};

export default function Promo() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);

  const load = useCallback(() => api.get("/promos").then(({ data }) => setRows(data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await api.post("/promos", {
        code: form.code,
        kind: form.kind,
        value: Number(form.value) || 0,
        min_order: Number(form.min_order) || 0,
        valid_from: form.valid_from,
        valid_to: form.valid_to,
        usage_limit: Number(form.usage_limit) || 0,
        note: form.note,
      });
      setForm(blank);
      load();
      toast.success(t("saved"));
    } catch (e) {
      toast.error(errMsg(e, "Gagal membuat promo"));
    }
  };

  const today = jstToday();
  const status = (p) => {
    if (p.valid_to && today > p.valid_to) return t("promo_expired");
    if (p.usage_limit && p.used_count >= p.usage_limit) return t("promo_used_up");
    if (p.valid_from && today < p.valid_from) return t("promo_scheduled");
    return t("promo_active");
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("promo_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("promo_sub")}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <p className="font-display font-semibold flex items-center gap-2">
          <Ticket size={18} className="text-primary" /> {t("promo_new")}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input
            data-testid="promo-code-input"
            placeholder={t("promo_code")}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          />
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="promo-kind-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">{t("promo_percent")}</SelectItem>
              <SelectItem value="amount">{t("promo_amount")}</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="promo-value-input"
            inputMode="numeric"
            placeholder={form.kind === "percent" ? "%" : "¥"}
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value.replace(/[^0-9]/g, "") })}
          />
          <Input
            data-testid="promo-min-input"
            inputMode="numeric"
            placeholder={t("promo_min")}
            value={form.min_order}
            onChange={(e) => setForm({ ...form, min_order: e.target.value.replace(/[^0-9]/g, "") })}
          />
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("from")}</p>
            <DatePicker
              testid="promo-from-input"
              className="h-10 w-full"
              value={form.valid_from}
              onChange={(v) => setForm({ ...form, valid_from: v })}
              placeholder={t("from")}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">{t("to")}</p>
            <DatePicker
              testid="promo-to-input"
              className="h-10 w-full"
              value={form.valid_to}
              onChange={(v) => setForm({ ...form, valid_to: v })}
              placeholder={t("to")}
            />
          </div>
          <Input
            data-testid="promo-limit-input"
            inputMode="numeric"
            placeholder={t("promo_limit")}
            value={form.usage_limit}
            onChange={(e) =>
              setForm({ ...form, usage_limit: e.target.value.replace(/[^0-9]/g, "") })
            }
          />
          <Button data-testid="save-promo-btn" onClick={save} className="h-10">
            <Plus size={16} className="mr-1" /> {t("save")}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="promo-list">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("no_data")}</p>
        )}
        {rows.map((p) => (
          <div
            key={p.id}
            data-testid={`promo-${p.code}`}
            className="bg-white border border-gray-200 rounded-2xl p-5 space-y-2"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-display text-lg font-semibold tracking-wide">{p.code}</span>
              <span className="text-xs px-2.5 py-1 rounded-full border bg-gray-50 text-gray-600">
                {status(p)}
              </span>
            </div>
            <p className="text-sm">
              {p.kind === "percent" ? `${p.value}%` : yen(p.value)}
              {p.min_order > 0 ? ` · min ${yen(p.min_order)}` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {p.valid_from || "—"} → {p.valid_to || "—"} ·{" "}
              {p.usage_limit ? `${p.used_count}/${p.usage_limit}` : `${p.used_count}x`}
            </p>
            {p.note && <p className="text-xs text-muted-foreground italic">{p.note}</p>}
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              data-testid={`delete-promo-${p.code}`}
              onClick={async () => {
                await api.delete(`/promos/${p.id}`);
                load();
                toast.success(t("saved"));
              }}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
